/**
 * PagoPage — suscripción SiprinIA: planes de registro, plan TronIA y pago por
 * PayPal/ePayco. La mensualidad se liquida VENCIDA el 5 de cada mes (la
 * primera prorrateada).
 *
 * [RGM] Reorganización aprobada por Rafael: la pantalla quedó huérfana y se
 * reconecta desde la franja amarilla (BarraPrueba) y desde Mi Cuenta → Pagos.
 * Estructura: encabezado → planes → módulos (tarjetas informativas + Fotos)
 * → TronIA → resumen (hoy / mensual) → acción (PayPal / ePayco / Probar
 * primero) → datos de pago y condiciones. TronIA se activa SIN cargo por
 * pasarela: su valor se suma a la mensualidad mínima.
 */

import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  CCard, CCardBody, CCardHeader, CCol, CRow, CButton, CAlert, CFormCheck, CFormSwitch, CSpinner,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilUser, cilPeople, cilBuilding, cilLockLocked, cilSettings, cilCreditCard, cilCalendar, cilWarning } from '@coreui/icons'
import { api } from '../api/client'
import { fmtMoneda } from '../utils/moneda'
import { abrirCheckoutEpayco } from '../utils/epayco'
import SubirPagoModal from '../components/SubirPagoModal'

interface PlanSiprinia {
  codigo: number; nombre: string; rutas: number; registro: number
  // Precio con la PROMO vigente (50% OFF del registro). [RGM] 2026-09-16.
  registroFinal: number
  informes: string; graficasNormal: number; graficasHoy: number
}
interface PlanTron {
  codigo: number; nombre: string; precio: number; consultas: number
}
interface Modulo {
  key: string; nombre: string; precio: number; tipo: string; porRuta: boolean
  precioConImpresora?: number; dependeDe?: string
}
interface Catalogo {
  mantenimientoMensual: number; prestamosActivosPorRuta: number
  prestamoAdicional: number; rutaAdicionalEmpresa: number; moneda: string
  descuentoRegistroPct?: number
  planes: PlanSiprinia[]; tronIA: PlanTron[]; modulos: Modulo[]; condiciones: string[]
}

/** Cotización del registro (GET /pago/cotizacion): total en COP, rutas
 *  actuales, PayPal en USD (+7%) y ePayco en COP (+5%) — misma fuente que el
 *  cobro del backend. El `epayco` es opcional por compatibilidad: si no viene,
 *  el botón muestra el total base. */
interface Cotizacion {
  totalCop: number
  rutas: number
  paypal: { totalUsd: number; recargoUsd: number; trm: number } | null
  // [RGM] 2026-10-23 — ePayco cobra el monto base + 5% (decisión Rafael).
  epayco?: { montoBaseCop: number; recargoCop: number; totalCop: number }
}

/** Selección del registro guardada para retomar donde quedó (sessionStorage).
 *  [RGM] Los valores viejos de conImpresion/conWhatsapp se ignoran: la compra
 *  de impresión/WhatsApp se quitó del registro (se activan por ruta desde
 *  Rutas). El módulo Facturación SÍ se compra en el registro (como Fotos). */
interface SeleccionGuardada {
  planCodigo?: number
  planTronia?: number
  conFotos?: boolean
  conFacturacion?: boolean
}

// El catálogo del backend identifica el módulo mensual de fotos por esta key
// (Constantes.cs ModuloSiprinia.CustomerPhotos — contrato con la API).
const KEY_FOTOS = 'CUSTOMER_PHOTOS'
// Módulo Facturación de la empresa (Constantes.cs ModuloSiprinia.Facturacion):
// pago ÚNICO que se compra en el registro (ConFacturacion) o desde Mi Cuenta.
const KEY_FACTURACION = 'FACTURACION'
const STORAGE_SELECCION = 'pagoSeleccion'

// Precios en la moneda de la empresa: fuente única utils/moneda.ts (fmtMoneda
// — símbolo configurado + máscara GX Z,ZZZ,ZZZ,ZZ9.99). [RGM] 2026-10-02 (F)
/** Monto en dólares con 2 decimales (cotización PayPal) — NO usa el símbolo
 *  de la empresa: la pasarela cotiza en USD real. Excepción documentada. */
const usd = (v?: number) =>
  `US$ ${Number(v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const PagoPage: React.FC = () => {
  const [cat, setCat] = useState<Catalogo | null>(null)
  const [planSel, setPlanSel] = useState<number>(1)
  const [tronSel, setTronSel] = useState<number>(0)
  const [fotos, setFotos] = useState(false)
  // [RGM] Módulo Facturación en el carrito del registro (ConFacturacion): se
  // suma al pago de HOY y al confirmarse activa EmpresaModuloFacturacion.
  const [facturacion, setFacturacion] = useState(false)
  // Cotización del servidor (total COP + rutas + USD de PayPal).
  const [cot, setCot] = useState<Cotizacion | null>(null)
  const [paypalOk, setPaypalOk] = useState(false)
  const [epaycoOk, setEpaycoOk] = useState(false)
  // Próximo cobro de la empresa (/pago/estado) — lo usa el modal compartido.
  const [proximoCobro, setProximoCobro] = useState('')
  // Modal compartido "Subir pago de Suscripción" (otro medio). [RGM]
  const [subirPagoAbierto, setSubirPagoAbierto] = useState(false)
  const [pagando, setPagando] = useState(false)
  const [pasarelaActiva, setPasarelaActiva] = useState<'paypal' | 'epayco' | null>(null)
  const [resultado, setResultado] = useState<{ ok: boolean; texto: string } | null>(null)
  // Retoma: había una selección guardada al montar (muestra la alerta azul).
  const [retoma, setRetoma] = useState(false)
  // Alerta amarilla "Modo de prueba" (se muestra al pulsar Probar primero).
  const [mostrarPrueba, setMostrarPrueba] = useState(false)
  const [params, setParams] = useSearchParams()
  // [RGM] 2026-09-29 (B6 aprobado): plan actual de la empresa — candados de la
  // pantalla de planes (solo SUBIR) y modo actualización (diferencia).
  const [planActual, setPlanActual] = useState(0)
  // [RGM] 2026-10-07 (feature F2 — diseño Rafael: "el motor de sistema...
  // debe tener los tres switch en las tres tarjetas"): MOTOR DEL SISTEMA —
  // radio de TRES estados: Solo Préstamos (default) · Híbrido · Solo
  // Facturación. Aplica INLINE (sin recargar). Tarifas y lógica no cambian.
  const [modo, setModo] = useState('PRESTAMOS')
  const [moduloFact, setModuloFact] = useState(0)
  const [guardandoModo, setGuardandoModo] = useState(false)
  // Estado efectivo del motor (radio): FACTURACION | HIBRIDO | PRESTAMOS.
  const motor = modo === 'FACTURACION' ? 'FACTURACION' : moduloFact === 1 ? 'HIBRIDO' : 'PRESTAMOS'
  // [RGM] 2026-10-07 (pedido Rafael: "cuando active híbrido en el switch se
  // activa en gris el chulito módulo de facturación y solo préstamos queda
  // activado en gris" + fix 2026-10-07 "si selecciono solo facturación no me
  // deja seleccionar híbrido": la marca pendiente de compra NO debe bloquear).
  const activarHibrido = async () => {
    if (motor === 'HIBRIDO') return // ya está (radio)
    if (motor === 'FACTURACION') {
      // Desde SOLO FACTURACIÓN: vuelven los préstamos. El módulo queda como
      // esté: si estaba pagado se conserva; si estaba pendiente, la marca de
      // compra sigue viva y el checkout lo cobra (nunca se regala).
      setGuardandoModo(true)
      try {
        const r = await api.post<any>('/cuenta/modo', {
          soloFacturacion: false,
          moduloFacturacion: moduloFact === 1,
        })
        setModo('PRESTAMOS')
        setModuloFact(Number(r?.moduloFacturacion ?? moduloFact))
      } catch (e: any) {
        alert(e?.message ?? 'No se pudo cambiar el motor del sistema.')
      } finally {
        setGuardandoModo(false)
      }
      return
    }
    // Desde Solo Préstamos: marca la compra del módulo (checkout, pago único).
    if (!facturacion) cambiarFacturacion(true)
  }
  const desactivarHibrido = async () => {
    if (facturacion) cambiarFacturacion(false)
    await cambiarMotor('PRESTAMOS')
  }
  const cambiarMotor = async (tipo: 'PRESTAMOS' | 'HIBRIDO' | 'FACTURACION') => {
    if (motor === tipo) return // el radio no se apaga tocándose a sí mismo
    // [RGM] 2026-10-07 (regla Rafael: "solo facturación SÍ cobra el plan más
    // los 500, porque internamente usa el motor de préstamos"): NUNCA se
    // regala el módulo — si no está pagado, entrar a SOLO FACTURACIÓN (o
    // Híbrido) MARCA la casilla del Módulo Facturación ($500.000 pago único)
    // para que el checkout lo cobre.
    const debeCobrarModulo = tipo !== 'PRESTAMOS' && moduloFact === 0 && !facturacion
    const etiqueta = tipo === 'PRESTAMOS' ? 'Solo Préstamos' : tipo === 'HIBRIDO' ? 'Híbrido' : 'Solo Facturación'
    const ok = window.confirm(
      `¿Cambiar tu sistema a "${etiqueta}"?\n\n` +
      (tipo === 'FACTURACION'
        ? 'Dejarás de ver el módulo de Préstamos y el Módulo Facturación queda fijo. '
        : tipo === 'HIBRIDO'
          ? 'Verás Préstamos + Facturación. '
          : 'Volverás al sistema de préstamos (sin Facturación). ') +
      (debeCobrarModulo
        ? `Se sumará el Módulo Facturación (${fmtMoneda(precioFacturacion)} — pago único). `
        : '') +
      'Tus tarifas y tus datos no cambian.')
    if (!ok) return
    setGuardandoModo(true)
    try {
      const r = await api.post<any>('/cuenta/modo', {
        soloFacturacion: tipo === 'FACTURACION',
        // El módulo NO se regala: si ya estaba pagado se conserva; si no,
        // queda pendiente y lo cobra el checkout (casilla marcada abajo).
        moduloFacturacion: tipo !== 'PRESTAMOS' && moduloFact === 1,
      })
      setModo(String(r?.modo ?? (tipo === 'FACTURACION' ? 'FACTURACION' : 'PRESTAMOS')))
      setModuloFact(Number(r?.moduloFacturacion ?? 0))
      if (debeCobrarModulo) cambiarFacturacion(true)
    } catch (e: any) {
      alert(e?.message ?? 'No se pudo cambiar el motor del sistema.')
    } finally {
      setGuardandoModo(false)
    }
  }

  useEffect(() => {
    api.get<Catalogo>('/pago/planes-siprinia').then(setCat).catch(() => {})
    api.get<any>('/cuenta').then((c) => setPlanActual(Number(c?.plan ?? 0))).catch(() => {})
    api.get<any>('/cuenta')
      .then((c) => {
        setModo(String(c?.modo ?? 'PRESTAMOS'))
        setModuloFact(Number(c?.moduloFacturacion ?? 0))
      })
      .catch(() => undefined)
    // [RGM] El API devuelve el camelCase de PayPalConfigurado → "payPalConfigurado"
    // (P mayúscula). Antes se leía "paypalConfigurado" (todo minúscula) → undefined
    // → paypalOk=false → el botón PayPal quedaba SIEMPRE deshabilitado. Se
    // aceptan ambos por robustez. ePayco ya coincidía.
    api.get<{
      payPalConfigurado?: boolean; paypalConfigurado?: boolean
      epaycoConfigurado?: boolean; proximoCobro?: string
    }>('/pago/estado')
      .then((e) => {
        setPaypalOk(!!(e.payPalConfigurado ?? e.paypalConfigurado))
        setEpaycoOk(!!e.epaycoConfigurado)
        setProximoCobro(e.proximoCobro ?? '')
      })
      .catch(() => {})
  }, [])

  // [RGM] Al volver del checkout con el botón ATRÁS, el navegador restaura la
  // página desde bfcache con `pagando` en true → los botones quedaban
  // deshabilitados hasta refrescar (reporte Rafael). Se limpia el estado al
  // restaurarse la página.
  useEffect(() => {
    const alRestaurar = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setPagando(false)
        setPasarelaActiva(null)
      }
    }
    window.addEventListener('pageshow', alRestaurar)
    return () => window.removeEventListener('pageshow', alRestaurar)
  }, [])

  // Retoma la selección guardada (plan / TronIA / fotos) al volver a la pantalla.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_SELECCION)
      if (!raw) return
      const sel = JSON.parse(raw) as SeleccionGuardada
      if (sel.planCodigo) setPlanSel(sel.planCodigo)
      if (typeof sel.planTronia === 'number') setTronSel(sel.planTronia)
      if (typeof sel.conFotos === 'boolean') setFotos(sel.conFotos)
      if (typeof sel.conFacturacion === 'boolean') setFacturacion(sel.conFacturacion)
      setRetoma(true)
    } catch {
      // Selección corrupta: se ignora (el usuario vuelve a elegir).
    }
  }, [])

  // Persiste la selección al CAMBIARLA. No se escribe al montar: la alerta de
  // retoma solo debe aparecer si el usuario ya había elegido antes.
  const persistir = (sel: SeleccionGuardada) =>
    sessionStorage.setItem(STORAGE_SELECCION, JSON.stringify(sel))
  const seleccionActual = (): SeleccionGuardada => ({
    planCodigo: planSel,
    planTronia: tronSel,
    conFotos: fotos,
    conFacturacion: facturacion,
  })
  const elegirPlan = (codigo: number) => {
    setPlanSel(codigo)
    persistir({ ...seleccionActual(), planCodigo: codigo })
  }
  const elegirTronia = (codigo: number) => {
    setTronSel(codigo)
    persistir({ ...seleccionActual(), planTronia: codigo })
  }
  const cambiarFotos = (valor: boolean) => {
    setFotos(valor)
    persistir({ ...seleccionActual(), conFotos: valor })
    // [RGM] 2026-09-28 (pedido Rafael): seleccionar Fotos lo ACTIVA de una —
    // sin cargo por pasarela, se cobra en la mensualidad del 5 (mismo patrón
    // del plan TronIA). Antes solo marcaba el carrito y no activaba nada.
    api.post('/cuenta/fotos', { activo: valor })
      .then(() => setResultado({
        ok: true,
        texto: valor
          ? 'Fotos del cliente ACTIVADO — se cobra en tu mensualidad.'
          : 'Fotos del cliente desactivado.',
      }))
      .catch((e: any) => setResultado({
        ok: false,
        texto: e?.message ?? 'No se pudo actualizar Fotos del cliente.',
      }))
  }
  const cambiarFacturacion = (valor: boolean) => {
    setFacturacion(valor)
    persistir({ ...seleccionActual(), conFacturacion: valor })
  }

  // [RGM] Cotización del servidor: el total en pesos sale de MontoAsync (misma
  // fuente que el cobro/binding) y el USD de PayPal de la TRM del día. Se
  // recalcula al cambiar la selección del registro (TronIA y Fotos).
  useEffect(() => {
    const qs = new URLSearchParams({
      planCodigo: String(planSel),
      planTronia: String(tronSel),
      conFotos: String(fotos),
      // El módulo Facturación se suma al total de HOY (registro) — misma
      // fuente del cobro (MontoAsync del backend).
      conFacturacion: String(facturacion),
    })
    api.get<Cotizacion>(`/pago/cotizacion?${qs}`)
      .then(setCot)
      .catch(() => setCot(null))
  }, [planSel, tronSel, fotos, facturacion])

  // Retorno del checkout: se recupera el contexto guardado y se captura.
  useEffect(() => {
    const token = params.get('token')
    if (params.get('paypal') === 'cancel') {
      setResultado({ ok: false, texto: 'Pago cancelado.' })
      setParams({})
      return
    }
    if (params.get('paypal') === 'ok' && token) {
      const ctxRaw = sessionStorage.getItem('pagoCtx')
      sessionStorage.removeItem('pagoCtx')
      const ctx = ctxRaw ? JSON.parse(ctxRaw) : { tipo: 'registro', planCodigo: planSel }
      setPasarelaActiva('paypal')
      setPagando(true)
      api.post<{ tipo?: string; valor?: number }>('/pago/paypal/capturar',
        { ordenId: token, ...ctx })
        .then((r) => {
          // Pago confirmado: la selección ya no se retoma en una próxima visita.
          sessionStorage.removeItem(STORAGE_SELECCION)
          setResultado({ ok: true, texto: `Pago recibido — ${r.tipo} (pendiente de confirmación).` })
        })
        .catch((err: any) => setResultado({ ok: false, texto: err?.message ?? 'No se pudo confirmar el pago.' }))
        .finally(() => { setPagando(false); setPasarelaActiva(null); setParams({}) })
    }
  }, [params, setParams, planSel])

  const pagar = async (pasarela: 'paypal' | 'epayco') => {
    // [RGM] 2026-09-29 (pedido Rafael: "debe exigirse elegir plan al pagar el
    // registro"): sin plan elegido no se paga (antes quedaban empresas sin plan).
    if (!planSel) {
      setResultado({ ok: false, texto: 'Elegí tu plan antes de pagar.' })
      return
    }
    // Guarda contra doble clic: `pagando` deshabilita los botones mientras se
    // crea la sesión y se abre el widget. En PayPal el finally corre antes de
    // la navegación (inofensivo); en ePayco se libera al abrirse el widget.
    setResultado(null)
    setPagando(true)
    setPasarelaActiva(pasarela)
    // Carrito del registro: TronIA (planTronia, si eligió plan) y Fotos
    // (conFotos) se suman a la liquidación del 5; el módulo Facturación
    // (conFacturacion) es pago único y se suma al total de hoy. Los módulos de
    // impresión y WhatsApp se activan por ruta desde Rutas (menú Activar).
    const ctx = {
      // [RGM] 2026-09-29 (B4/B6): con plan activo es ACTUALIZACIÓN (se cobra
      // la diferencia); sin plan es el registro normal.
      tipo: planActual > 0 ? 'actualizar-plan' : 'registro',
      planCodigo: planSel,
      planTronia: tronSel || undefined,
      conFotos: fotos,
      conFacturacion: facturacion,
    }
    try {
      if (pasarela === 'paypal') {
        // PayPal: approvalUrl → redirect. El contexto queda en sessionStorage
        // para capturar la orden al volver (?paypal=ok). ePayco NO lo usa: la
        // activación la hace el webhook de confirmación.
        sessionStorage.setItem('pagoCtx', JSON.stringify(ctx))
        const r = await api.post<{ approvalUrl?: string }>('/pago/paypal/orden', ctx)
        if (r.approvalUrl) window.location.href = r.approvalUrl
        return
      }
      // ePayco checkout v2 [RGM] 2026-09-15: el backend crea la sesión y aquí
      // se abre el widget con checkout-v2.js.
      const r = await api.post<{ sessionId?: string; test?: boolean }>('/pago/epayco/orden', ctx)
      if (!r.sessionId) {
        setResultado({ ok: false, texto: 'No se pudo iniciar el pago con ePayco.' })
        return
      }
      await abrirCheckoutEpayco(r.sessionId, !!r.test)
      // Inicio ePayco exitoso: la selección ya no se retoma en una próxima visita.
      sessionStorage.removeItem(STORAGE_SELECCION)
    } catch (err: any) {
      setResultado({ ok: false, texto: err?.message ?? 'No se pudo iniciar el pago.' })
    } finally {
      setPagando(false)
      setPasarelaActiva(null)
    }
  }

  // Valores derivados del catálogo del servidor (nada hardcodeado en montos).
  const plan = cat?.planes.find((p) => p.codigo === planSel)
  const tron = cat?.tronIA.find((t) => t.codigo === tronSel)
  const moduloFotos = cat?.modulos.find((m) => m.key === KEY_FOTOS)
  // Módulo Facturación del catálogo (pago único por empresa): el precio sale
  // SIEMPRE del API (override de entorno incluido), nunca hardcodeado.
  const moduloFacturacion = cat?.modulos.find((m) => m.key === KEY_FACTURACION)
  const modulosUnicos = (cat?.modulos ?? []).filter((m) => m.tipo === 'Unico')
  const rutasPlan = plan?.rutas ?? 0
  const precioFotos = moduloFotos?.precio ?? 0
  const precioFacturacion = moduloFacturacion?.precio ?? 0
  const matricula = plan?.registroFinal ?? 0
  // El total del día es el del servidor (cotización = MontoAsync); sin
  // cotización cae a la matrícula del catálogo.
  const totalHoy = cot?.totalCop ?? matricula
  const tronMes = tronSel > 0 ? (tron?.precio ?? 0) : 0
  const fotosMes = fotos ? precioFotos * rutasPlan : 0
  const mensualidadMinima = (cat?.mantenimientoMensual ?? 0) + tronMes + fotosMes

  return (
    <CRow className="g-3">
      {/* ENCABEZADO + PLANES */}
      <CCol xs={12}>
        <CCard className="border-0 shadow-sm">
          <CCardHeader className="fw-semibold d-flex align-items-center gap-2">
            <CIcon icon={cilLockLocked} /> Cuenta creada — Elige tu plan
          </CCardHeader>
          <CCardBody>
            {retoma && (
              <CAlert color="info">
                Continuamos desde donde quedó tu registro. Revisá tu plan, módulos y servicios
                seleccionados y completá el pago.
              </CAlert>
            )}
            <div className="fw-semibold mb-2">Planes SiprinIA — solo nuevos registros</div>
            <CRow className="g-3">
              {(cat?.planes ?? []).map((p) => {
                // [RGM] 2026-09-29 (B6 aprobado Rafael): candados — solo se
                // puede SUBIR de plan. Con plan activo: tarjetas ≤ actual
                // bloqueadas (badge) y las mayores muestran la DIFERENCIA
                // (promo 50% incluida).
                const bloqueado = p.codigo <= planActual
                const PROMO: Record<number, number> = { 1: 200000, 2: 400000, 3: 600000 }
                const esUpgrade = planActual > 0 && p.codigo > planActual
                return (
                <CCol md={4} key={p.codigo}>
                  <CCard
                    className={`h-100 ${planSel === p.codigo ? 'border-primary border-3' : ''} ${bloqueado ? 'opacity-50' : ''}`}
                    style={{ cursor: bloqueado ? 'not-allowed' : 'pointer' }}
                    onClick={() => { if (!bloqueado) elegirPlan(p.codigo) }}
                  >
                    <CCardBody>
                      <div className="fw-bold d-flex align-items-center gap-2">
                        <CIcon icon={p.codigo === 1 ? cilUser : p.codigo === 2 ? cilPeople : cilBuilding} />
                        {p.nombre}
                        {bloqueado && (
                          <span className="badge bg-secondary ms-auto">
                            {p.codigo === planActual ? 'Plan actual' : 'Ya incluido'}
                          </span>
                        )}
                        {!bloqueado && planSel === p.codigo && <span className="badge bg-primary ms-auto">Seleccionado</span>}
                      </div>
                      <div className="text-body-secondary" style={{ fontSize: 13 }}>
                        {p.codigo === 3 ? '3 o más rutas' : `${p.rutas} ruta${p.rutas > 1 ? 's' : ''}`}
                      </div>
                      {/* PROMO 50% OFF del registro ([RGM] decisión Rafael):
                          precio original tachado + el precio que cobra la pasarela. */}
                      <div className="fs-4 fw-bold mt-2 d-flex align-items-center gap-2">
                        {esUpgrade ? fmtMoneda(PROMO[p.codigo] - PROMO[planActual]) : fmtMoneda(p.registroFinal)}
                        {!esUpgrade && (
                          <span className="badge bg-warning text-dark">
                            {cat?.descuentoRegistroPct ?? 50}% OFF
                          </span>
                        )}
                      </div>
                      <div className="text-body-secondary" style={{ fontSize: 13 }}>
                        {esUpgrade ? (
                          <>Diferencia para actualizar a {p.nombre}</>
                        ) : (
                          <><s>{fmtMoneda(p.registro)}</s> antes</>
                        )}
                      </div>
                      <div style={{ fontSize: 13 }}>
                        {p.informes === 'todos' ? 'Todos los informes desbloqueados' : 'Informes básicos'}
                      </div>
                      <div className="mt-2" style={{ fontSize: 13 }}>
                        Gráficas Gerenciales: <s>{fmtMoneda(p.graficasNormal)}</s>{' '}
                        <span className="fw-bold text-success">Hoy {fmtMoneda(p.graficasHoy)} GRATIS</span>
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol>
                )
              })}
            </CRow>
            <div className="mt-3" style={{ fontSize: 13 }}>
              <CIcon icon={cilSettings} className="me-1" />
              Mantenimiento mensual: <strong>{fmtMoneda(cat?.mantenimientoMensual)}</strong> (cualquier plan,
              hasta {cat?.prestamosActivosPorRuta} préstamos/ruta · préstamo adicional {fmtMoneda(cat?.prestamoAdicional)})
              · Ruta adicional Empresa: {fmtMoneda(cat?.rutaAdicionalEmpresa)}/mes
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      {/* [RGM] 2026-10-07 (feature F2 — pedido Rafael: "el motor de sistema
          va en la parte superior, debajo de planes"; "los tres switch en las
          tres tarjetas"): radio Motor del sistema — Solo Préstamos (default)
          · Híbrido · Solo Facturación. Aplica INLINE. Tarifas no cambian. */}
      <CCol xs={12}>
        <CCard className="border-0 shadow-sm">
          <CCardHeader className="fw-semibold">Motor del sistema</CCardHeader>
          <CCardBody>
            <div className="text-body-secondary small mb-2">
              Define el motor de tu operación financiera: cartera, facturación y
              recaudo de cartera móvil por app, con asistencia de inteligencia
              artificial. Solo cambia los módulos habilitados — tus tarifas y tus
              datos permanecen intactos.
            </div>
            <CRow className="g-3">
              <CCol md={4}>
                <CCard className={`h-100 shadow-sm ${motor === 'PRESTAMOS' ? 'border-primary border-2' : 'opacity-75'}`}>
                  <CCardBody className="py-2">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div className="fw-semibold">Solo Préstamos</div>
                      <CFormSwitch
                        id="motor-prestamos-pago"
                        label=""
                        // [RGM] 2026-10-07 ("no me está dejando cambiar de
                        // selección"): el gris es indicador visual — TODOS los
                        // switches quedan CLICABLES (solo se bloquean mientras
                        // guarda). Incluido (gris) en Híbrido; apagado en Solo
                        // Facturación; clic = volver a Solo Préstamos.
                        checked={motor !== 'FACTURACION'}
                        disabled={guardandoModo}
                        onChange={() => desactivarHibrido()}
                      />
                    </div>
                    <small className="text-body-secondary">Para empresas dedicadas al otorgamiento y cobro de préstamos: desembolsos, cuotas, abonos, mora y recaudo de cartera móvil por app.</small>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol md={4}>
                <CCard className={`h-100 shadow-sm ${motor === 'HIBRIDO' || (motor !== 'FACTURACION' && facturacion) ? 'border-primary border-2' : 'opacity-75'}`}>
                  <CCardBody className="py-2">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div className="fw-semibold">Híbrido</div>
                      <CFormSwitch
                        id="motor-hibrido-pago"
                        label=""
                        // [RGM] 2026-10-07 ("no me está dejando cambiar de
                        // selección"): clicable SIEMPRE (también desde Solo
                        // Facturación — el usuario puede pasar a Híbrido
                        // explícitamente, con su confirmación).
                        checked={motor === 'HIBRIDO' || (motor !== 'FACTURACION' && facturacion)}
                        disabled={guardandoModo}
                        onChange={(e) => (e.target.checked ? activarHibrido() : desactivarHibrido())}
                      />
                    </div>
                    <small className="text-body-secondary">
                      Para empresas que prestan y además venden mercancías a
                      crédito: cartera de préstamos y cuentas por cobrar de
                      facturación, unificadas en una sola ruta de recaudo de
                      cartera móvil por app.
                    </small>
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol md={4}>
                <CCard className={`h-100 shadow-sm ${motor === 'FACTURACION' ? 'border-primary border-2' : ''}`}>
                  <CCardBody className="py-2">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div className="fw-semibold">Solo Facturación</div>
                      <CFormSwitch
                        id="motor-facturacion-pago"
                        label=""
                        // [RGM] 2026-10-07 ("no me está dejando cambiar de
                        // selección"): clicable SIEMPRE (el gris es visual); la
                        // confirmación avisa que dejarás de ver Préstamos.
                        checked={motor === 'FACTURACION'}
                        disabled={guardandoModo}
                        onChange={() => cambiarMotor('FACTURACION')}
                      />
                    </div>
                    <small className="text-body-secondary">
                      Para comercios que venden de contado o a crédito y recaudan
                      por rutas, sin otorgar préstamos: la venta a crédito genera
                      su plan de cuotas y se recauda con cartera móvil por app.
                    </small>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>

      {/* MÓDULOS Y SERVICIOS ADICIONALES (solo con catálogo cargado) */}
      {cat && (
        <CCol xs={12}>
          <CCard className="border-0 shadow-sm">
            <CCardHeader className="fw-semibold">Módulos y servicios adicionales</CCardHeader>
            <CCardBody>
              <div className="fw-semibold mb-2">Pago único</div>
              <CRow className="g-2">
                {modulosUnicos.map((m) => (
                  <CCol md={4} key={m.key}>
                    <CCard className="h-100">
                      <CCardBody className="py-2">
                        <div className="fw-semibold">{m.nombre}</div>
                        <div className="fw-bold">
                          {fmtMoneda(m.precio)}
                          {m.precioConImpresora ? (
                            <span className="text-body-secondary fw-normal">
                              {' '}/ {fmtMoneda(m.precioConImpresora)} con impresora
                            </span>
                          ) : null}
                        </div>
                        {/* [RGM] Solo informativo: impresión/WhatsApp se activan
                            por ruta (menú Activar de Rutas); Facturación es por
                            EMPRESA y se marca abajo en el registro. */}
                        <div className="text-body-secondary" style={{ fontSize: 12 }}>
                          {m.porRuta ? 'Se activa en cada ruta.' : 'Se activa para toda la empresa.'}
                        </div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                ))}
              </CRow>
              {/* [RGM] Módulo Facturación: pago ÚNICO por EMPRESA que SÍ se
                  compra en el registro (mismo patrón de Fotos): al marcarlo
                  viaja ConFacturacion en la cotización y el checkout, y el
                  resumen de hoy suma su precio (el total lo calcula el
                  servidor — MontoAsync). [RGM] 2026-10-07 ("devuelve a su
                  lugar... sin quitarle la activación en gris"): la casilla
                  vuelve AQUÍ y se marca AUTOMÁTICA EN GRIS cuando el motor es
                  Híbrido o Solo Facturación (no editable mientras el motor la
                  implique). */}
              {moduloFacturacion && (
                <div className="mt-3">
                  <CFormCheck
                    id="facturacion"
                    label={`Activar Módulo Facturación — ${fmtMoneda(precioFacturacion)} (pago único)`}
                    checked={facturacion || motor === 'FACTURACION' || motor === 'HIBRIDO'}
                    disabled={guardandoModo || facturacion || motor === 'FACTURACION' || motor === 'HIBRIDO'}
                    onChange={(e) => cambiarFacturacion(e.target.checked)}
                  />
                  <div className="text-body-secondary" style={{ fontSize: 12 }}>
                    Se activa en tu empresa al confirmarse el pago
                  </div>
                </div>
              )}
              {moduloFotos && (
                <div className="mt-3">
                  <div className="fw-semibold mb-1">Servicios mensuales</div>
                  <CFormCheck
                    id="fotos"
                    label={`Activar Foto del cliente — ${fmtMoneda(precioFotos)}/mes por ruta`}
                    checked={fotos}
                    disabled={fotos}
                    onChange={(e) => cambiarFotos(e.target.checked)}
                  />
                  <div className="text-body-secondary" style={{ fontSize: 12 }}>
                    {fotos
                      ? 'Activado — para desactivar, contactá al administrador.'
                      : 'Se cobrará en la mensualidad mínima'}
                  </div>
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      )}

      {/* PLAN TronIA (activación sin cargo: se suma a la mensualidad) */}
      {cat && (
        <CCol xs={12}>
          <CCard className="border-0 shadow-sm">
            <CCardHeader className="fw-semibold">Elige tu plan de TronIA</CCardHeader>
            <CCardBody>
              <CRow className="g-3">
                {cat.tronIA.map((t) => (
                  <CCol md={6} lg={3} key={t.codigo}>
                    <CCard
                      className={`h-100 ${tronSel === t.codigo ? 'border-primary border-3' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => elegirTronia(t.codigo)}
                    >
                      <CCardBody className="text-center">
                        <div className="fw-bold">{t.nombre}</div>
                        {t.codigo === 0 ? (
                          <div className="text-body-secondary" style={{ fontSize: 13 }}>
                            Gratis · {t.consultas} consultas de prueba
                          </div>
                        ) : (
                          <div style={{ fontSize: 13 }}>
                            <span className="fs-6 fw-bold">{fmtMoneda(t.precio)}</span>
                            /mes ({t.consultas} consultas)
                          </div>
                        )}
                      </CCardBody>
                    </CCard>
                  </CCol>
                ))}
              </CRow>
              <div className="text-body-secondary mt-3" style={{ fontSize: 13 }}>
                TronIA se activa sin cargo: su valor se suma a tu mensualidad.
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      )}

      {/* RESUMEN: total de hoy + cobro mensual (solo con catálogo cargado) */}
      {cat && (
        <>
          <CCol md={6}>
            <CCard className="border-0 shadow-sm h-100 bg-body-secondary">
              <CCardBody>
                <div className="fw-semibold mb-2"><CIcon icon={cilCreditCard} className="me-1" />Total para pagar hoy</div>
                <div className="d-flex justify-content-between">
                  <span>Matrícula ({plan?.nombre ?? ''})</span>
                  <span>{fmtMoneda(matricula)}</span>
                </div>
                {/* Módulo Facturación elegido en el registro: pago único que
                    suma al total de hoy (el total real viene de la cotización
                    del servidor). */}
                {facturacion && moduloFacturacion && (
                  <div className="d-flex justify-content-between">
                    <span>Módulo Facturación</span>
                    <span>{fmtMoneda(precioFacturacion)}</span>
                  </div>
                )}
                <hr className="my-2" />
                <div className="d-flex justify-content-between fw-bold">
                  <span>Total hoy</span>
                  <span>{fmtMoneda(totalHoy)}</span>
                </div>
                <div className="text-body-secondary mt-2" style={{ fontSize: 12 }}>
                  Montos calculados por el servidor. El pago con PayPal tiene un recargo del 7%.
                </div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md={6}>
            <CCard className="border-0 shadow-sm h-100">
              <CCardBody>
                <div className="fw-semibold mb-2"><CIcon icon={cilCalendar} className="me-1" />Cobro mensual desde el 5 de cada mes</div>
                <div className="d-flex justify-content-between">
                  <span>Mantenimiento</span>
                  <span>{fmtMoneda(cat.mantenimientoMensual)}</span>
                </div>
                {tronSel > 0 && tron && (
                  <div className="d-flex justify-content-between">
                    <span>TronIA {tron.nombre}</span>
                    <span>{fmtMoneda(tron.precio)}/mes</span>
                  </div>
                )}
                {fotos && moduloFotos && (
                  <div className="d-flex justify-content-between">
                    <span>Fotos de clientes</span>
                    <span>{fmtMoneda(fotosMes)}/mes</span>
                  </div>
                )}
                <hr className="my-2" />
                <div className="d-flex justify-content-between fw-bold">
                  <span>Mensualidad mínima</span>
                  <span>{fmtMoneda(mensualidadMinima)}</span>
                </div>
                <div className="text-body-secondary mt-2" style={{ fontSize: 12 }}>
                  Incluye préstamos activos por ruta. El valor puede aumentar según la cantidad de
                  préstamos activos al finalizar el período.
                  <br />
                  La primera mensualidad es prorrateada (se paga vencida el 5).
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </>
      )}

      {/* ZONA DE ACCIÓN FINAL */}
      <CCol md={6}>
        <CCard className="border-0 shadow-sm h-100">
          <CCardBody>
            {mostrarPrueba && (
              <CAlert color="warning">
                <div className="fw-bold"><CIcon icon={cilWarning} className="me-1" />Modo de prueba</div>
                <div>
                  La prueba incluye únicamente un plan básico. Los planes, módulos y servicios
                  seleccionados no se activarán hasta realizar el pago.
                </div>
                {/* [RGM] 2026-09-29 (pedido Rafael): que el cliente entienda
                    las DOS vías de pago — suscripción (pasarela) o subir pago
                    por OTROS MEDIOS (transferencia o giro). */}
                <div className="mt-2">
                  Para pagar tenés dos vías: <strong>Pagar la Suscripción</strong> (planes de
                  arriba, con PayPal o ePayco) o <strong>Subir Pago (Otros medios)</strong> si
                  hiciste transferencia o giro.
                </div>
                <div className="d-flex gap-2 mt-3">
                  <CButton color="primary" size="sm" onClick={() => { window.location.href = '/home' }}>
                    Sí, probar ahora
                  </CButton>
                  <CButton color="secondary" variant="outline" size="sm" onClick={() => setMostrarPrueba(false)}>
                    Cancelar
                  </CButton>
                </div>
              </CAlert>
            )}
            <div className="d-flex flex-column gap-2">
              <CButton color="primary" disabled={pagando || !paypalOk} onClick={() => pagar('paypal')}>
                {/* [RGM] 2026-10-02 (pedido Rafael: "debe quedar un botón
                    girando mientras sale la pasarela"): spinner mientras conecta. */}
                {pagando && pasarelaActiva === 'paypal' ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    Conectando con PayPal...
                  </>
                ) : (
                  <>
                    <i className="fab fa-paypal me-2" />
                    {cot?.paypal
                      ? `Pagar con PayPal — ${usd(cot.paypal.totalUsd)}`
                      : 'Pagar con PayPal (USD +7%)'}
                  </>
                )}
              </CButton>
              <CButton color="success" disabled={pagando || !epaycoOk} onClick={() => pagar('epayco')}>
                {pagando && pasarelaActiva === 'epayco' ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    Conectando con Epayco...
                  </>
                ) : cot ? (
                  `Pagar con ePayco 🇨🇴 — ${fmtMoneda(cot.epayco?.totalCop ?? cot.totalCop)}`
                ) : (
                  'Pagar con ePayco 🇨🇴'
                )}
              </CButton>
              <CButton
                color="secondary"
                variant="outline"
                disabled={pagando}
                onClick={() => setMostrarPrueba(true)}
              >
                Probar primero
              </CButton>
            </div>
            {/* [RGM] 2026-10-23 — ePayco traslada al cliente el 5% de comisión
                de la pasarela (decisión Rafael). */}
            <small className="text-body-secondary d-block mt-2">
              El pago con ePayco incluye un 5% de comisión de la pasarela.
            </small>
            {resultado && (
              <CAlert color={resultado.ok ? 'success' : 'danger'} className="mt-3 mb-0">
                {resultado.texto}
              </CAlert>
            )}
          </CCardBody>
        </CCard>
      </CCol>

      {/* DATOS DE PAGO (la mensualidad se paga en Mi Cuenta → Pagos) */}
      <CCol md={6}>
        <CCard className="border-0 shadow-sm h-100">
          <CCardHeader className="fw-semibold">Datos de pago</CCardHeader>
          <CCardBody style={{ fontSize: 14 }}>
            <div className="fw-semibold">PAGOS EN COLOMBIA</div>
            <div>Cuenta de Ahorros Bancolombia: <strong>74116820670</strong></div>
            <div><strong>RAFAEL GRANADA MENDOZA</strong> — C.C. <strong>94469600</strong></div>
            {/* [RGM] 2026-10-23 (pedido Rafael): otra forma de pago de la
                mensualidad — QR Bre-B (Davivienda/Daviplata) y llave
                @DAVICYBERTRONICA. Se paga desde la app bancaria y después se
                sube el comprobante con "Subir Pago (Otros medios)". */}
            <div className="mt-3">
              <div className="fw-semibold">PAGA ESCANEANDO EL QR (BRE-B)</div>
              <div className="text-body-secondary small">
                Desde tu app bancaria (Davivienda, Daviplata, Nequi o cualquier
                entidad habilitada) o a la llave <strong>@DAVICYBERTRONICA</strong>
              </div>
              <img
                src="/qr-bre-b-davivienda.png"
                alt="QR Bre-B Davivienda — CyberTronica (@DAVICYBERTRONICA)"
                className="rounded-3 border mt-2"
                style={{ maxWidth: 260, width: '100%' }}
              />
            </div>
            <div className="mt-2 fw-semibold">GIROS INTERNACIONALES</div>
            <div>Western Unión — RAFAEL GRANADA MENDOZA — DESTINO: <strong>COLOMBIA</strong></div>
            {/* [RGM] Pagó por OTRO medio (transferencia/giro): abre el modal
                COMPARTIDO (components/SubirPagoModal) — el pago queda
                pendiente de confirmación del superadmin. */}
            <div className="mt-3">
              <CButton color="primary" variant="outline"
                onClick={() => setSubirPagoAbierto(true)}>
                Subir Pago (Otros medios)
              </CButton>
              <div className="text-body-secondary small mt-1">
                Transferencia o giro: subí tu comprobante y queda pendiente de confirmación del administrador
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      {cat?.condiciones?.length ? (
        <CCol xs={12}>
          <div className="text-body-secondary" style={{ fontSize: 12 }}>
            {cat.condiciones.map((c) => <div key={c}>• {c}</div>)}
          </div>
        </CCol>
      ) : null}

      {/* [RGM] Modal COMPARTIDO "Subir pago de Suscripción" (el form GX del
          panel subirpagosistema con comprobante obligatorio): queda pendiente
          de confirmación del superadmin. */}
      <SubirPagoModal
        visible={subirPagoAbierto}
        onClose={() => setSubirPagoAbierto(false)}
        proximoCobro={proximoCobro}
        // [RGM] 2026-09-29 (pedido Rafael: "si el pago es por registro mandar
        // de una vez pago de Registro en tipo de pago"): esta página es el
        // REGISTRO → el combo viene preseleccionado en 1 (Registro).
        tipoInicial={1}
      />
    </CRow>
  )
}

export default PagoPage
